// src/api/interceptor.ts
import { API_URL } from './config'
import { getEventUid } from './eventScope'

/**
 * Instala, uma única vez no boot, um envelope sobre `window.fetch`.
 *
 * O app tem 31 chamadas `fetch` cruas espalhadas por oito arquivos. Migrar todas para um
 * cliente próprio às vésperas do evento seria mexer em cada caminho de tela — inclusive
 * os que não dá para exercitar sem o hardware da porta. O envelope entrega o que
 * realmente falta, sem tocar em nenhuma delas:
 *
 *   - X-Event-Uid em toda requisição à API, para o escopo de evento funcionar quando o
 *     servidor deixar de ter um evento padrão;
 *   - teto de espera, porque numa rede de centro de convenções uma conexão pendurada
 *     trava a fila da porta sem nada aparecer na tela.
 *
 * Requisições para outros destinos passam intactas. Quem já usa `apiFetch` não é afetado:
 * o cabeçalho só é acrescentado quando ainda não existe.
 */

const TIMEOUT_PADRAO_MS = 8000

let instalado = false

function ehChamadaDaApi(url: string): boolean {
  const base = API_URL.replace(/\/+$/, '')
  if (url.startsWith(base)) return true
  // Caminhos relativos (quando API_URL é relativa, como no admin).
  return base.startsWith('/') && url.startsWith(base)
}

export function instalarInterceptorDeRede(): void {
  if (instalado || typeof window === 'undefined' || !window.fetch) return
  instalado = true

  const fetchOriginal = window.fetch.bind(window)

  window.fetch = async (entrada: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof entrada === 'string' ? entrada : entrada instanceof URL ? entrada.href : entrada.url

    if (!ehChamadaDaApi(url)) return fetchOriginal(entrada as any, init)

    const opcoes: RequestInit = { ...(init || {}) }

    const headers = new Headers(opcoes.headers || (typeof entrada !== 'string' && !(entrada instanceof URL) ? entrada.headers : undefined))
    const eventUid = getEventUid()
    if (eventUid && !headers.has('X-Event-Uid')) headers.set('X-Event-Uid', eventUid)
    opcoes.headers = headers

    // Respeita um AbortSignal já fornecido pela chamada; só cria um quando não há.
    let timer: ReturnType<typeof setTimeout> | undefined
    if (!opcoes.signal) {
      const controller = new AbortController()
      timer = setTimeout(() => controller.abort(), TIMEOUT_PADRAO_MS)
      opcoes.signal = controller.signal
    }

    try {
      return await fetchOriginal(url, opcoes)
    } finally {
      if (timer) clearTimeout(timer)
    }
  }
}
